import React from "react";
import { Link } from "react-router-dom";
import { Check, Star, ArrowRight } from "lucide-react";
import { inr } from "@/lib/format";

// Bronze · Silver · Gold tier theming — consistent across Home plans, Plans page
// and the comparison table on /plans.
const TIER = {
  individual: {
    label: "Bronze · Individual",
    topBar: "from-[#C28B6F] via-[#A8704F] to-[#8B5A3C]",
    badge: "bg-gradient-to-r from-[#B8826E] to-[#8B5A3C] text-white shadow",
    ring: "ring-[#B8826E]/30",
    accentText: "text-[#8B5A3C]",
    glow: "shadow-[0_22px_50px_-28px_rgba(139,90,60,0.55)]",
  },
  husband_wife: {
    label: "Silver · Husband Wife",
    topBar: "from-[#B6C2CC] via-[#8A98A4] to-[#5E6B78]",
    badge: "bg-gradient-to-r from-[#8A98A4] to-[#5E6B78] text-white shadow",
    ring: "ring-[#7E8B96]/40",
    accentText: "text-[#5E6B78]",
    glow: "shadow-[0_22px_50px_-28px_rgba(94,107,120,0.5)]",
  },
  family: {
    label: "Gold · Family",
    topBar: "from-[#E3BC58] via-[#C89B3C] to-[#9C7421]",
    badge: "bg-gradient-to-r from-[#C89B3C] to-[#9C7421] text-white shadow",
    ring: "ring-[#C89B3C]/45",
    accentText: "text-[#9C7421]",
    glow: "shadow-[0_22px_50px_-28px_rgba(200,155,60,0.55)]",
  },
};

export default function PlanCard({ plan, index = 0 }) {
  const t = TIER[plan.tier] || TIER.individual;
  const base = plan.base_price ?? plan.price;
  const gstPct = plan.gst_percent ?? 18;
  const final = plan.price ?? Math.round(base * (1 + gstPct / 100));

  return (
    <div
      className={`plan-card fade-up delay-${Math.min(index + 1, 3)} relative rounded-3xl bg-white pt-10 px-8 pb-8 ring-1 ${t.ring} flex flex-col h-full overflow-hidden ${plan.highlight ? `${t.glow} ring-2` : ""}`}
      data-testid={`plan-card-${plan.tier}`}
    >
      {/* Top color bar — Bronze / Silver / Gold */}
      <div className={`absolute top-0 inset-x-0 h-1.5 bg-gradient-to-r ${t.topBar}`} />

      {plan.highlight && (
        <div className="absolute top-3 right-4 flex items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-r from-[#A53A2A] to-[#7A2A1E] text-white text-[10px] uppercase tracking-[0.18em] font-semibold shadow-lg">
          <Star className="h-3 w-3 fill-current" /> Most Loved
        </div>
      )}

      <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-[0.2em] self-start ${t.badge}`}>
        {plan.tier === "family" && <Star className="h-3 w-3 fill-current" />}
        {t.label}
      </div>

      <h3 className="mt-5 font-display text-3xl font-bold text-[#0F2540]">{plan.name}</h3>
      <p className="mt-1 text-sm text-[#5C6B7A]">{plan.tagline || `For ${plan.family_members} member${plan.family_members > 1 ? "s" : ""}`}</p>

      <div className="mt-6 flex items-baseline gap-1">
        <span className={`font-display text-5xl font-bold ${t.accentText}`}>{inr(final)}</span>
        <span className="text-sm text-[#5C6B7A]">/{plan.duration_months === 12 ? "yr" : `${plan.duration_months}mo`}</span>
      </div>
      <p className="mt-1 text-xs text-[#5C6B7A]">Price includes {gstPct}% GST</p>
      <p className="mt-1 text-[11px] text-[#5C6B7A]">
        Covers {plan.family_members} member{plan.family_members > 1 ? "s" : ""} · {plan.duration_months} months
      </p>

      <div className="my-6 h-px bg-gradient-to-r from-transparent via-[#E1E8F0] to-transparent" />

      <ul className="space-y-2.5 flex-1">
        {(plan.benefits || []).map((b, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-[#0F2540]">
            <Check className="h-4 w-4 mt-0.5 text-[#1F7A3D] flex-shrink-0" />
            <span>{b}</span>
          </li>
        ))}
      </ul>

      <Link
        to={`/join?plan=${plan.plan_id}`}
        className={`mt-8 w-full justify-center group ${plan.highlight ? "btn-primary" : "btn-ghost"}`}
        data-testid={`buy-${plan.tier}-btn`}
      >
        Buy Now <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
      </Link>
    </div>
  );
}
